import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

type Step = 'enterEmail' | 'enterCode';

const SuperuserLogin: React.FC = () => {
    const { requestSuperuserOtp, verifySuperuserOtp } = useAuth();
    const navigate = useNavigate();

    const [step, setStep] = useState<Step>('enterEmail');
    const [email, setEmail] = useState('');
    const [code, setCode] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [info, setInfo] = useState<string | null>(null);

    const inputStyle: React.CSSProperties = {
        width: '100%', padding: '10px 12px', fontSize: '16px',
        border: '1px solid #ddd', borderRadius: '6px', boxSizing: 'border-box',
        outline: 'none', transition: 'border-color 0.2s',
    };

    const labelStyle: React.CSSProperties = {
        display: 'block', fontWeight: 'bold', fontSize: '13px', marginBottom: '6px', color: '#444',
    };

    const handleRequestCode = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        setInfo(null);
        setLoading(true);
        try {
            await requestSuperuserOtp(email.trim());
            setInfo('Om e-postadressen finns hos oss är en sexsiffrig kod på väg. Den gäller i 10 minuter.');
            setStep('enterCode');
            setCode('');
        } catch (err: any) {
            setError(err?.response?.data?.message || err.message || 'Kunde inte skicka koden');
        } finally {
            setLoading(false);
        }
    };

    const handleVerifyCode = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        setInfo(null);
        setLoading(true);
        try {
            await verifySuperuserOtp(email.trim(), code.trim());
            navigate('/tenant-selector');
        } catch (err: any) {
            setError(err?.response?.data?.message || err.message || 'Fel kod, eller så har den gått ut');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div style={{
            minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
            backgroundColor: '#f0f2f5', fontFamily: 'Arial, sans-serif', padding: '16px',
        }}>
            <div style={{
                backgroundColor: 'white', borderRadius: '10px', padding: '32px 28px',
                boxShadow: '0 4px 24px rgba(0,0,0,0.10)', width: '100%', maxWidth: '380px',
                boxSizing: 'border-box',
            }}>
                <div style={{ textAlign: 'center', marginBottom: '32px' }}>
                    <h2 style={{ margin: '0 0 8px', color: '#1a1a2e', fontSize: '22px' }}>
                        🔑 Logga in som huvudadmin
                    </h2>
                    <p style={{ color: '#888', fontSize: '14px', margin: 0 }}>
                        Administration av DevIoT
                    </p>
                </div>

                {error && (
                    <div style={{
                        backgroundColor: '#f8d7da', color: '#721c24', padding: '10px 14px',
                        borderRadius: '6px', marginBottom: '20px', fontSize: '14px', textAlign: 'center',
                    }}>
                        {error}
                    </div>
                )}
                {info && !error && (
                    <div style={{
                        backgroundColor: '#d1ecf1', color: '#0c5460', padding: '10px 14px',
                        borderRadius: '6px', marginBottom: '20px', fontSize: '14px', textAlign: 'center',
                    }}>
                        {info}
                    </div>
                )}

                {step === 'enterEmail' ? (
                    <form onSubmit={handleRequestCode}>
                        <div style={{ marginBottom: '24px' }}>
                            <label style={labelStyle}>E-postadress</label>
                            <input
                                type="email"
                                required
                                autoFocus
                                autoComplete="email"
                                value={email}
                                onChange={e => setEmail(e.target.value)}
                                style={inputStyle}
                                onFocus={e => { e.target.style.borderColor = '#007bff'; }}
                                onBlur={e => { e.target.style.borderColor = '#ddd'; }}
                            />
                        </div>
                        <button
                            type="submit"
                            disabled={loading}
                            style={{
                                width: '100%', padding: '11px', fontSize: '15px', fontWeight: 'bold',
                                backgroundColor: loading ? '#6c9bd1' : '#007bff', color: 'white',
                                border: 'none', borderRadius: '6px', cursor: loading ? 'not-allowed' : 'pointer',
                                transition: 'background-color 0.2s',
                            }}
                        >
                            {loading ? 'Skickar…' : 'Skicka kod'}
                        </button>
                    </form>
                ) : (
                    <form onSubmit={handleVerifyCode}>
                        <div style={{ marginBottom: '24px' }}>
                            <label style={labelStyle}>Sexsiffrig kod</label>
                            <input
                                type="text"
                                inputMode="numeric"
                                pattern="\d{6}"
                                maxLength={6}
                                required
                                autoFocus
                                autoComplete="one-time-code"
                                value={code}
                                onChange={e => setCode(e.target.value.replace(/\D/g, ''))}
                                style={{ ...inputStyle, letterSpacing: '6px', fontSize: '20px', textAlign: 'center' }}
                                onFocus={e => { e.target.style.borderColor = '#007bff'; }}
                                onBlur={e => { e.target.style.borderColor = '#ddd'; }}
                            />
                        </div>
                        <button
                            type="submit"
                            disabled={loading || code.length !== 6}
                            style={{
                                width: '100%', padding: '11px', fontSize: '15px', fontWeight: 'bold',
                                backgroundColor: (loading || code.length !== 6) ? '#6c9bd1' : '#007bff', color: 'white',
                                border: 'none', borderRadius: '6px', cursor: (loading || code.length !== 6) ? 'not-allowed' : 'pointer',
                                transition: 'background-color 0.2s',
                            }}
                        >
                            {loading ? 'Kontrollerar…' : 'Logga in'}
                        </button>
                        <div style={{ textAlign: 'center', marginTop: '14px' }}>
                            <button
                                type="button"
                                onClick={() => { setStep('enterEmail'); setError(null); setInfo(null); setCode(''); }}
                                style={{ background: 'none', border: 'none', color: '#007bff', cursor: 'pointer', fontSize: '13px', textDecoration: 'underline' }}
                            >
                                Använd en annan e-postadress
                            </button>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
};

export default SuperuserLogin;

